import { useCallback, useRef, useState } from 'react';
// import { store } from '@uhg-uhc-mobile/federation/lib/zustand/store';

import type { IChatApiHeaders, IUseChatApiReturn } from '../types/ChatApi.types';
import { streamChatResponse } from '../services/ChatApiService';
import { getSecureRandomInt } from '../utils/random.utils';
import { useChatContext } from '../context/ChatConfigContext';
import { useLocationPermission } from '../location/useLocationPermission';

const generateSessionId = (): string => {
  let sessionId = '';

  for (let index = 0; index < 33; index += 1) {
    sessionId += getSecureRandomInt(16).toString(16);
  }

  return sessionId;
};

const getUserIdentity = (userID: string, enterpriseId: string): IChatApiHeaders['userIdentity'] => {
  return {
    userId: userID,
    eid: enterpriseId,
  };
};

type IResolvedLocation = { latitude: number; longitude: number } | null;
type IFederationLocation = { position?: { lat?: number; lng?: number } };

const getSearchParam = (name: string): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  return new URLSearchParams(window.location.search).get(name)?.trim() ?? '';
};

// The API only needs a non-empty `e-id` (enterprise/tenant id). The RN app sources
// it from the logged-in federation session; the standalone web app has no such
// session, so we fall back to a placeholder (matching the RN network trace) and
// allow overriding via `?e_id=` / `?eid=` query param or the VITE_CHAT_ENTERPRISE_ID env.
const DEFAULT_ENTERPRISE_ID = 'your-e-id';

const getChatUserId = (): string =>
  getSearchParam('user_id') || getSearchParam('userId') || import.meta.env.VITE_CHAT_USER_ID || '';

const getUserLocation = (location: IResolvedLocation): IChatApiHeaders['location'] => {
  if (!location) {
    return {
      latitude: '',
      longitude: '',
    };
  }

  return {
    latitude: String(location.latitude),
    longitude: String(location.longitude),
  };
};

const generateUserId = (prefix: string): string => {
  let userId = prefix;
  for (let i = 0; i < 10; i++) {
    userId += getSecureRandomInt(16).toString(16);
  }
  // store.getState().setHsidUuid(userId); // TODO
  return userId;
};

export const useChatApi = (): IUseChatApiReturn => {
  const { healthExToken } = useChatContext();
  const federationLocation = null as IFederationLocation | null;

  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [streamedText, setStreamedText] = useState('');
  const [latestJsonResponse, setLatestJsonResponse] = useState<unknown | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const { getCurrentLocation } = useLocationPermission();
  const userId = getChatUserId();

  const sessionRef = useRef({
    sessionId: generateSessionId(),
    userId: userId ? userId : generateUserId('web-actor-'),
  });

  const getVariablesForMockedQuery = (): Record<string, unknown> | undefined => {
    return undefined;
  };

  const defaultHeaders = useCallback(async (): Promise<IChatApiHeaders> => {
    const resolvedLocation: IResolvedLocation = federationLocation?.position
      ? {
          latitude: federationLocation.position.lat ?? 0,
          longitude: federationLocation.position.lng ?? 0,
        }
      : await getCurrentLocation();

    const resolvedEnterpriseId = DEFAULT_ENTERPRISE_ID;

    return {
      sessionId: sessionRef.current.sessionId,
      userIdentity: getUserIdentity(sessionRef.current.userId, resolvedEnterpriseId),
      location: getUserLocation(resolvedLocation),
      healthExToken,
    };
  }, [federationLocation, getCurrentLocation, healthExToken]);

  const resetStreamedText = useCallback(() => {
    setStreamedText('');
    setError(null);
  }, []);

  const sendMessage = useCallback(
    async (query: string, variables: Record<string, unknown> = {}) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setIsStreaming(true);
      setError(null);
      setStreamedText('');
      setLatestJsonResponse(null);

      try {
        const mergedVariables = {
          ...getVariablesForMockedQuery(),
          ...variables,
        };
        const headers = await defaultHeaders();
        await streamChatResponse(
          { query, variables: mergedVariables },
          headers,
          (chunk: string) => {
            setStreamedText((prev) => prev + chunk);
          },
          (jsonData: unknown) => {
            setLatestJsonResponse(jsonData);
          },
          { signal: abortController.signal },
        );
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        const chatError = err instanceof Error ? err : new Error('Chat API request failed');
        setError(chatError);
      } finally {
        if (abortControllerRef.current === abortController) {
          setIsStreaming(false);
        }
      }
    },
    [defaultHeaders],
  );

  return {
    sendMessage,
    isStreaming,
    error,
    streamedText,
    latestJsonResponse,
    resetStreamedText,
  };
};
